/**
 * Brief 185 — the dashboard activity stream. Reads GET /v1/account/activity and
 * renders each ledger row with a product chip, a one-line description, the
 * acting credential, the amount, an object deep-link, and — for LLM rows — an
 * expandable cost-plus receipt (provider cost → markup → charged → ceiling).
 *
 * It is the workhorse behind Home (with actor filter chips), the per-agent
 * mini-stream on Connected agents (fixed `actor`), and the LLM / cputools
 * workspaces (fixed `product` / `servicePrefix`). Filters are SERVER-SIDE so
 * pagination stays correct.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronRight, ExternalLink, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { ProductChip } from '@/components/ProductChip';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ActorBadge, type ActorLabels } from '@/components/ActorBadge';
import { formatMicros, formatMicrosAdaptive } from '@/components/MoneyAmount';
import {
  type ActivityMoneyRow,
  type ActivityQuotaRow,
  type ActivityQuery,
  type ActivityRow,
  activityKey,
  fetchActivity,
} from '@/lib/queries';
import { ApiError } from '@/lib/fetcher';

/** Map a ledger subject to an in-app deep link, when the object has a page. */
function deepLinkFor(row: ActivityMoneyRow): string | null {
  if (!row.subjectId) return null;
  switch (row.subjectType) {
    case 'baton':
      return `/dashboard/batons/${row.subjectId}`;
    case 'envelope':
      return `/dashboard/esign`;
    case 'verification':
      return `/dashboard/idverify`;
    default:
      return null;
  }
}

/** Strip the `bat_` prefix for the in-app baton deep link. */
function batonLinkFor(batonId: string): string {
  return `/dashboard/batons/${batonId.startsWith('bat_') ? batonId.slice(4) : batonId}`;
}

/** Human-readable byte size (binary units) for quota draws. */
function formatBytes(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return `${raw} B`;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(2) : v.toFixed(1)} ${units[i]}`;
}

export interface ActivityFilter {
  key: string;
  label: string;
  /** The actor id this chip filters to (omit for the "All" chip). */
  actor?: string;
}

function ReceiptDetail({ row }: { row: ActivityMoneyRow }) {
  const r = row.receipt;
  if (!r) return null;
  const line = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between gap-6">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
  return (
    <div className="mt-2 space-y-1 rounded-md border bg-muted/30 p-3 text-xs">
      {r.model ? line('Model', r.model) : null}
      {r.inputTokens !== undefined ? line('Input tokens', r.inputTokens) : null}
      {r.outputTokens !== undefined ? line('Output tokens', r.outputTokens) : null}
      {r.providerCostMicros !== undefined
        ? line('Provider cost', formatMicros(r.providerCostMicros, { minorDigits: 6 }))
        : null}
      {r.markupPct !== undefined ? line('Markup', `${r.markupPct}%`) : null}
      {r.chargedMicros !== undefined
        ? line('Charged', <strong>{formatMicros(r.chargedMicros, { minorDigits: 6 })}</strong>)
        : null}
      {/* The captured ledger row's amount is the ACTUAL, so the authorized
          ceiling rides on the receipt (Brief 185). Pre-fix rows lack it — fall
          back to the row amount only then. */}
      {line(
        'Authorized ceiling',
        formatMicros(r.ceilingMicros ?? Number(row.amountMicros), { minorDigits: 6 }),
      )}
      {r.truncated ? (
        <div className="pt-1 text-amber-600 dark:text-amber-400">
          Output hit the cap — result was truncated; the charge stands.
        </div>
      ) : null}
    </div>
  );
}

/**
 * Brief 187 Phase 4D — a baton byte-QUOTA draw shown UNDER its compute charge
 * (grouped by correlationId). It carries no money amount: storage/egress were
 * prepaid at baton create, so this draws the prepaid quota, never the balance.
 */
function QuotaLine({ q }: { q: ActivityQuotaRow }) {
  const label = q.dimension === 'storage' ? 'storage written' : 'egress drawn';
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
      <div className="flex min-w-0 items-center gap-1.5">
        <span aria-hidden className="text-muted-foreground/60">
          ↳
        </span>
        <Link to={batonLinkFor(q.batonId)} className="truncate hover:text-foreground">
          {q.batonId}
        </Link>
        <span>· {label}</span>
      </div>
      <span className="whitespace-nowrap font-mono tabular-nums">{formatBytes(q.bytes)}</span>
    </div>
  );
}

function Row({
  row,
  labels,
  quota = [],
}: {
  row: ActivityMoneyRow;
  labels: ActorLabels;
  /** Baton quota draws grouped under this compute charge (Brief 187). */
  quota?: ActivityQuotaRow[];
}) {
  const [open, setOpen] = React.useState(false);
  const link = deepLinkFor(row);
  const hasReceipt = Boolean(row.receipt);
  const isCredit = row.direction === 'credit';
  // Brief 185 — the whole row toggles the receipt when there is one (the
  // chevron is just the affordance), so a row-click expands as expected.
  const toggle = () => hasReceipt && setOpen((o) => !o);
  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={toggle}
          className={hasReceipt ? 'mt-0.5 text-muted-foreground' : 'mt-0.5 invisible'}
          aria-label={open ? 'Collapse receipt' : 'Expand receipt'}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div
            className={'flex flex-wrap items-center gap-2' + (hasReceipt ? ' cursor-pointer' : '')}
            onClick={toggle}
          >
            <ProductChip product={row.product} />
            <span className="truncate text-sm">{row.reason}</span>
            {link ? (
              <Link
                to={link}
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            ) : null}
            {hasReceipt && !open ? (
              <span className="text-xs text-muted-foreground">· receipt</span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <ActorBadge actor={row.actor} labels={labels} />
            <span className="text-xs text-muted-foreground">
              {format(parseISO(row.createdAt), 'MMM d, HH:mm')}
            </span>
          </div>
          {/* Brief 187 — baton quota draws this op made, shown next to (never
              blended into) the money charge. Always visible (they ARE the
              two-tier-honesty surface), not gated behind the receipt toggle. */}
          {quota.length > 0 ? (
            <div className="mt-1.5 space-y-1">
              {quota.map((q) => (
                <QuotaLine key={q.id} q={q} />
              ))}
            </div>
          ) : null}
          {open ? <ReceiptDetail row={row} /> : null}
        </div>
        <div
          className={
            'whitespace-nowrap font-mono text-sm tabular-nums ' +
            (isCredit ? 'text-emerald-600 dark:text-emerald-400' : '')
          }
        >
          {isCredit ? '+' : '−'}
          {formatMicrosAdaptive(row.amountMicros)}
        </div>
      </div>
    </li>
  );
}

/**
 * A QUOTA event whose compute charge is NOT on this page (no money row to
 * group under) — rendered standalone, visually distinct from a money row: a
 * byte amount in muted type, no ± money, a baton deep-link.
 */
function StandaloneQuotaRow({ q }: { q: ActivityQuotaRow }) {
  const label = q.dimension === 'storage' ? 'storage written' : 'egress drawn';
  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 invisible">
          <ChevronRight className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ProductChip product="baton" />
            <Link to={batonLinkFor(q.batonId)} className="truncate text-sm hover:underline">
              {q.batonId}
            </Link>
            <span className="text-sm text-muted-foreground">· {label}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {format(parseISO(q.createdAt), 'MMM d, HH:mm')}
          </div>
        </div>
        <div className="whitespace-nowrap font-mono text-sm tabular-nums text-muted-foreground">
          {formatBytes(q.bytes)}
        </div>
      </div>
    </li>
  );
}

/**
 * Render the unified timeline: money rows carry their grouped baton quota draws
 * (same correlationId, page-local) inline; a quota event whose compute charge
 * is NOT on this page renders standalone. Order follows the server's
 * created-at-desc ordering — a money row sits where the op's charge landed; its
 * draws nest under it rather than re-sorting into the flat stream.
 */
function renderTimeline(rows: ActivityRow[], labels: ActorLabels): React.ReactNode {
  // correlationId → its quota draws (only correlated ones can group).
  const quotaByCorr = new Map<string, ActivityQuotaRow[]>();
  for (const r of rows) {
    if (r.kind === 'quota' && r.correlationId) {
      const list = quotaByCorr.get(r.correlationId) ?? [];
      list.push(r);
      quotaByCorr.set(r.correlationId, list);
    }
  }
  // The correlationIds that have a money row on THIS page — their quota draws
  // group under that row and are skipped in the flat pass.
  const groupedCorr = new Set<string>();
  for (const r of rows) {
    if (r.kind === 'money' && r.correlationId && quotaByCorr.has(r.correlationId)) {
      groupedCorr.add(r.correlationId);
    }
  }

  const out: React.ReactNode[] = [];
  for (const row of rows) {
    if (row.kind === 'money') {
      const grouped = row.correlationId ? quotaByCorr.get(row.correlationId) ?? [] : [];
      out.push(<Row key={row.id} row={row} labels={labels} quota={grouped} />);
    } else {
      // Quota row: skip if it groups under a money row present on this page.
      if (row.correlationId && groupedCorr.has(row.correlationId)) continue;
      out.push(<StandaloneQuotaRow key={row.id} q={row} />);
    }
  }
  return out;
}

export function ActivityStream({
  baseQuery = {},
  filters,
  labels = {},
  pageSize = 25,
}: {
  /** Fixed query terms (product / servicePrefix / actor) for a workspace view. */
  baseQuery?: Partial<ActivityQuery>;
  /** Optional actor filter chips (Home). Omit for a fixed workspace stream. */
  filters?: ActivityFilter[];
  labels?: ActorLabels;
  pageSize?: number;
}) {
  const [activeFilter, setActiveFilter] = React.useState<string>(filters?.[0]?.key ?? 'all');
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(pageSize);

  const chip = filters?.find((f) => f.key === activeFilter);
  const query: ActivityQuery = {
    ...baseQuery,
    limit,
    offset,
    ...(chip?.actor ? { actor: chip.actor } : {}),
  };

  const q = useQuery({ queryKey: activityKey(query), queryFn: () => fetchActivity(query) });

  return (
    <div className="space-y-3">
      {filters && filters.length > 1 ? (
        <div className="flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setActiveFilter(f.key);
                setOffset(0);
              }}
              className={
                'rounded-full border px-3 py-1 text-xs transition-colors ' +
                (activeFilter === f.key
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border text-muted-foreground hover:text-foreground')
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}

      {q.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : q.error ? (
        <Alert variant="destructive">
          <AlertTitle>
            Couldn't load activity ({q.error instanceof ApiError ? q.error.code : 'unknown'})
          </AlertTitle>
          <AlertDescription>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => q.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : q.data && q.data.data.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Nothing here yet"
          description="Activity from your agents, keys, and dashboard shows up here as you spend."
        />
      ) : q.data ? (
        <ErrorBoundary
          label="activity"
          fallback={
            <Alert variant="destructive">
              <AlertTitle>Activity unavailable</AlertTitle>
              <AlertDescription>
                This view hit a display error. The rest of your dashboard is unaffected.
                <Button size="sm" variant="outline" className="ml-2" onClick={() => q.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          }
        >
          <ul className="divide-y">
            {renderTimeline(q.data.data, labels)}
          </ul>
          <Pagination
            total={q.data.total}
            limit={q.data.limit}
            offset={q.data.offset}
            onChange={({ limit: l, offset: o }) => {
              setLimit(l);
              setOffset(o);
            }}
          />
        </ErrorBoundary>
      ) : null}
    </div>
  );
}
